import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link, useParams } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import toast, { Toaster } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import countries from '../data/countries.json';
import styles from './signup.module.css';
import useCompany from '../redux/hooks/useCompany';
import Navbar from '../components/navbar';

const UpdateCompany = () => {
  // Initial state and variables ---------------------------------------------------\\

  const { id } = useParams();
  const url = `${process.env.REACT_APP_API}/admin/company/update/${id}`;
  const { token } = useSelector((state) => state.user);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [feedback, setFeedback] = useState('');
  const [apiErrs, setApierrs] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [locked, setLock] = useState(true);
  const { loading, company } = useCompany(token, id);
  const navigate = useNavigate();

  // UseEffect to populate from fields. ---------------------------------------------------\\
  useEffect(() => {
    reset(company);
  }, [loading]);

  // Handle signup ---------------------------------------------------\\
  const handleUpdate = async (data) => {
    const {
      raisonSociale,
      siret,
      pays,
      ville,
      postcode,
      address,
      contact,
      email,
      dg,
      ca,
    } = data;
    const myHeaders = new Headers();
    myHeaders.append('Accept', 'application/json');
    myHeaders.append('Content-Type', 'application/json');

    try {
      const newCompany = {
        raisonSociale,
        siret,
        pays,
        ville,
        postcode,
        address,
        contact,
        email,
        dg,
        ca,
      };
      setSpinner(true);
      const response = await axios.put(`${url}`, newCompany, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        toast.success(response.data.message, {
          duration: 4000,
        });
        setSpinner(false);
        setApierrs([]);
        setLock(true);
      } else {
        setFeedback(
          `Error Code: ${response.status}. \n OOOOPS.. ${response.data.message}`
        );
      }
    } catch (error) {
      setApierrs(error.response.data.errors);
      setSpinner(false);
    }
  };

  // Map API Errors ---------------------------------------------------\\
  const mapErrs = (obj) => {
    const keys = Object.keys(obj);
    return (
      <p className='help-block text-danger my-2'>
        {`${keys[0]}: `}
        {obj[keys[0]]}
      </p>
    );
  };

  // Rendered page ---------------------------------------------------\\
  return loading ? (
    // Waiting data loading after async request...
    <>
      <Helmet>
        <title>Badgers SAS: Update company</title>
        <meta name='page' content='update company profile' />
      </Helmet>
      <div className='loading'>
        <img src='/img/loading.gif' alt='Loading' className='loading-img' />
      </div>
    </>
  ) : (
    <>
      <Helmet>
        <title>Badgers SAS: Update company</title>
        <meta name='page' content='update company profile' />
      </Helmet>
      <Navbar displayMob={true} />
      <div className={styles.pan}>
        <Toaster position='top-center' reverseOrder={false} />
        <form
          onSubmit={handleSubmit(handleUpdate)}
          className='row g-3'
          action=''
        >
          {/* ----------------------- */}
          <div className=''>
            <Link className='page-links' to='/'>
              <FontAwesomeIcon icon='home' />{' '}
            </Link>
            <span className='link-span'>
              <FontAwesomeIcon icon='angle-right' />
            </span>
            <Link className='page-links' to='/companies'>
              COMPANIES{' '}
            </Link>
            <span className='link-span'>
              <FontAwesomeIcon icon='angle-right' />
            </span>
            <Link className='page-links' to={`/company/${company?.id}`}>
              {`${company?.raisonSociale?.toUpperCase()} `}
            </Link>
            <span className='link-span'>
              <FontAwesomeIcon icon='angle-right' />
            </span>
            <span className='link-span'>UPDATE COMPANY</span>
          </div>
          {/* ----------------------- */}
          <div className='col-12 d-flex justify-content-center my-4'>
            <h5 className='h5'>
              Update Company <FontAwesomeIcon icon='building' />
            </h5>
          </div>
          <div className={`${styles.card} row`}>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.raisonSociale}
                readOnly={locked}
                className='form-control'
                type='text'
                name='raisonSociale'
                id='raisonSociale'
                placeholder='Corporate Name'
                {...register('raisonSociale', {
                  required: 'Please enter the company name.',
                  maxLength: 60,
                  minLength: 2,
                })}
              />
              {errors?.raisonSociale && (
                <p className='help-block text-danger my-2'>
                  {errors.raisonSociale?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.siret}
                readOnly={locked}
                className='form-control'
                type='text'
                name='siret'
                id='siret'
                placeholder='SIRET, Registration Number'
                {...register('siret', {
                  maxLength: 30,
                  minLength: 3,
                })}
              />
              {errors?.siret && (
                <p className='help-block text-danger my-2'>
                  {errors.siret?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.email}
                readOnly={locked}
                className='form-control'
                type='email'
                name='email'
                id='email'
                placeholder='Email Address'
                {...register('email', {
                  required: 'Please enter a valid email address.',
                })}
              />
              {errors?.email && (
                <p className='help-block text-danger my-2'>
                  {errors.email?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.contact}
                readOnly={locked}
                className='form-control'
                type='text'
                name='contact'
                id='contact'
                placeholder='Contact Number'
                {...register('contact', {
                  required: 'Please enter a valid telephone number.',
                })}
              />
              {errors?.contact && (
                <p className='help-block text-danger my-2'>
                  {errors.contact?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.address}
                readOnly={locked}
                className='form-control'
                type='text'
                name='address'
                id='address'
                placeholder='Company street address'
                {...register('address', {
                  required: 'Please enter your company address.',
                })}
              />
              {errors?.address && (
                <p className='help-block text-danger my-2'>
                  {errors.address?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.postcode}
                readOnly={locked}
                className='form-control'
                type='text'
                name='postcode'
                id='postcode'
                placeholder='Post Code'
                {...register('postcode', {})}
              />
              {errors?.postcode && (
                <p className='help-block text-danger my-2'>
                  {errors.postcode?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <select
                defaultValue={company?.pays}
                //   readOnly={locked}
                id='pays'
                name='pays'
                className='form-select form-control'
                {...register('pays', {
                  required: 'Please enter the country, company is based in.',
                })}
              >
                <option className='default-select' value=''>
                  Select Your Location
                </option>
                {countries.map((obj, idx) => (
                  <option
                    value={obj.name}
                    key={idx}
                    selected={obj.name === company?.pays}
                  >
                    {obj.name}
                  </option>
                ))}
              </select>
              {errors?.pays && (
                <p className='help-block text-danger my-2'>
                  {errors.pays?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.ville}
                readOnly={locked}
                className='form-control'
                type='text'
                name='ville'
                id='ville'
                placeholder='City or State'
                {...register('ville', {
                  required: 'Please enter the city or state name.',
                })}
              />
              {errors?.ville && (
                <p className='help-block text-danger my-2'>
                  {errors.ville?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.dg}
                readOnly={locked}
                className='form-control'
                type='text'
                name='dg'
                id='dg'
                placeholder='Key Person'
                {...register('dg', {
                  required: "Please enter key person's name.",
                  minLength: 8,
                })}
              />
              {errors?.dg && (
                <p className='help-block text-danger my-2'>
                  {errors.dg?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={company?.ca}
                readOnly={locked}
                className='form-control'
                type='number'
                name='ca'
                id='ca'
                placeholder='Annual Turnover'
                {...register('ca', {
                  min: 1,
                })}
              />
              {errors?.ca && (
                <p className='help-block text-danger my-2'>
                  {errors.ca?.message}
                </p>
              )}
            </div>
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={!locked}
                    onChange={(evt) => setLock(!evt.target.checked)}
                  />
                }
                label='Modify'
              />
            </div>
            {apiErrs.validation && (
              <p className='help-block text-danger my-2'>
                {errors.validation?.message}
              </p>
            )}
            {apiErrs.length > 0 && apiErrs.map((obj) => mapErrs(obj))}
            <div className='newsletter'>
              <p className='help-block text-danger my-2'>{feedback}</p>
            </div>
            <div className='row justify-content-center mt-4'>
              {spinner ? (
                <button type='' className={`btn ${styles.btnsignup}`}>
                  Sending data ..
                  <div
                    className='spinner-grow spinner-grow-sm'
                    role='status'
                  ></div>
                </button>
              ) : (
                <button
                  type='submit'
                  disabled={locked}
                  className={`btn ${styles.btnsignup}`}
                >
                  Update Company <FontAwesomeIcon icon='building' />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default UpdateCompany;
